import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts } from '../utils/blogUtils';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../data/business';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

interface BlogPost {
  id: string;
  title: string;
  description: string;
  publishDate: string;
  formattedDate: string;
  imageUrl: string | null;
  url: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadPosts = async () => {
      try {
        const blogPosts = await fetchBlogPosts();
        setPosts(blogPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadPosts();
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Blog | Thaprachan Thai</title>
        <meta name="description" content="Discover delicious Thai cuisine, cooking tips, and restaurant updates on the Thaprachan Thai blog." />
        <meta name="keywords" content="Thai food blog, Thai cooking, Seattle Thai restaurant, Thai recipes" />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
      </Helmet>
      
      <Header />
      
      <main className="pt-24 pb-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold font-serif mb-3 text-gray-900">Thaprachan Thai Blog</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover authentic Thai cuisine, cooking tips, and the latest updates from our restaurant
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-lg text-gray-500">Loading blog posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 border rounded-lg">
              <p className="text-lg text-gray-500">No blog posts found. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link 
                  to={`/blog/${post.id}`}
                  key={post.id}
                  className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-1 duration-300"
                >
                  {post.imageUrl && (
                    <div className="aspect-w-16 aspect-h-9">
                      <img 
                        src={post.imageUrl} 
                        alt={post.title}
                        className="object-cover w-full h-48" 
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="text-sm text-primary mb-2">{post.formattedDate}</p>
                    <h2 className="text-xl font-bold mb-2 text-gray-900 font-serif">
                      {post.title.replace(' | Thaprachan Thai', '')}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.description}</p>
                    <span
                      className="inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                    >
                      Read More
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default Blog; 